/* Shared bits of the sign-in pane: language switch, pane header,
   back link and the password-strength meter. */
import type { Lang } from '@/types';
import { ic, sized } from '@/components/Icons';
import { useI18n } from '@/i18n';

export function LangToggle({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  const isEn = lang === 'en';
  return (
    <button
      type="button"
      role="switch"
      aria-label="language"
      aria-checked={isEn}
      onClick={() => setLang(isEn ? 'es' : 'en')}
      style={{
        position: 'relative', display: 'flex', alignItems: 'center',
        width: 74, height: 28, padding: 2,
        background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 999,
        cursor: 'pointer', flex: 'none',
      }}
    >
      {/* sliding knob */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute', top: 2, left: 2,
          width: 'calc(50% - 2px)', height: 'calc(100% - 4px)',
          background: 'var(--surface)', borderRadius: 999, boxShadow: 'var(--shadow-1)',
          transform: isEn ? 'translateX(100%)' : 'translateX(0)',
          transition: 'transform .22s cubic-bezier(.4,0,.2,1)',
        }}
      />
      {(['es', 'en'] as Lang[]).map((l) => (
        <span
          key={l}
          style={{
            position: 'relative', zIndex: 1, flex: 1, textAlign: 'center',
            fontWeight: 700, fontSize: 11, letterSpacing: '.04em',
            color: lang === l ? 'var(--brand-ink)' : 'var(--text-2)',
            transition: 'color .22s ease',
          }}
        >
          {l.toUpperCase()}
        </span>
      ))}
    </button>
  );
}

export function PaneHeader({ title }: { title: string }) {
  const { lang, setLang } = useI18n();
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-0.012em' }}>{title}</h2>
      <LangToggle lang={lang} setLang={setLang} />
    </div>
  );
}

export function BackLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn ghost"
      style={{ alignSelf: 'flex-start', padding: '4px 8px', marginLeft: -8, color: 'var(--text-2)', fontSize: 12, fontWeight: 600 }}
    >
      {sized(ic.chevL, 14)} {label}
    </button>
  );
}

/* ---------- password strength ---------- */

/** El mínimo tiene que ser el MISMO que `MIN_PASSWORD` en el servidor
 *  (server/src/auth/routes.ts). Estuvo en 8 mientras el servidor exigía
 *  12: el formulario daba por buena una contraseña de nueve caracteres,
 *  habilitaba el botón, y el servidor la rechazaba después de escribirla
 *  dos veces. `ForcePasswordChange` ya se defendía comprobando 12 por su
 *  cuenta; el resto de las pantallas no. */
export const MIN_PASSWORD = 12;

export function passwordChecks(p: string) {
  return {
    length: p.length >= MIN_PASSWORD,
    upper: /[A-Z]/.test(p),
    number: /[0-9]/.test(p),
  };
}

export function passwordScore(p: string): number {
  const c = passwordChecks(p);
  return (c.length ? 1 : 0) + (c.upper ? 1 : 0) + (c.number ? 1 : 0);
}

export function PasswordStrength({ p }: { p: string }) {
  const { lang, t } = useI18n();
  const score = passwordScore(p);
  const c = passwordChecks(p);
  const color = score >= 3 ? 'var(--ok)' : score === 2 ? 'var(--warn)' : 'var(--err)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 3, background: i < score ? color : 'var(--line)', transition: 'background .2s' }} />
        ))}
      </div>
      {p ? (
        <div style={{ fontSize: 11, color: 'var(--text-2)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ color: c.length ? 'var(--ok)' : 'var(--text-3)' }}>
            {c.length ? sized(ic.check, 11) : '·'}{' '}
            {lang === 'es' ? `${MIN_PASSWORD}+ caracteres` : `${MIN_PASSWORD}+ chars`}
          </span>
          <span style={{ color: c.upper ? 'var(--ok)' : 'var(--text-3)' }}>
            {c.upper ? sized(ic.check, 11) : '·'} {lang === 'es' ? 'Mayúscula' : 'Uppercase'}
          </span>
          <span style={{ color: c.number ? 'var(--ok)' : 'var(--text-3)' }}>
            {c.number ? sized(ic.check, 11) : '·'} {lang === 'es' ? 'Número' : 'Number'}
          </span>
        </div>
      ) : null}
      {/* strength word is announced for screen readers; the bars carry it visually */}
      <span className="sr-only" aria-live="polite">
        {score === 0 ? '' : score === 1 ? t('login.pw_strength_weak') : score === 2 ? t('login.pw_strength_med') : t('login.pw_strength_strong')}
      </span>
    </div>
  );
}
